<template>
  <MainLayout>

    <!-- -- Hero ----------------------------------------------------------------- -->
    <section class="relative bg-gradient-hero py-7 lg:py-10 overflow-hidden">
      <div class="absolute inset-0 hex-pattern pointer-events-none"></div>
      <div class="absolute left-1/4 top-0 w-[400px] h-[400px] bg-secondary/10 rounded-full blur-[100px] pointer-events-none"></div>
      <div class="absolute right-16 bottom-8 text-white/5 text-[140px] font-bold select-none hidden lg:block">+</div>

      <div class="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
        <div class="flex justify-center mb-4 animate-fade-up" style="animation-delay:0.1s">
          <div class="flex items-center gap-3">
            <span class="w-8 h-0.5 bg-secondary rounded-full"></span>
            <span class="text-secondary text-xs font-bold uppercase tracking-[0.2em]">Our Latest Products</span>
            <span class="w-8 h-0.5 bg-secondary rounded-full"></span>
          </div>
        </div>
        <h1 class="text-5xl sm:text-6xl font-black text-white mb-4 animate-fade-up" style="animation-delay:0.2s">
          Our <span class="text-secondary">Products</span>
        </h1>
        <p class="text-white/60 text-base max-w-xl mx-auto mb-6 animate-fade-up" style="animation-delay:0.3s">
          Premium biomedical equipment and medical supplies sourced from certified international manufacturers.
        </p>
        <nav class="flex items-center justify-center gap-2 text-sm text-white/50 animate-fade-up" style="animation-delay:0.4s">
          <RouterLink to="/" class="hover:text-secondary transition-colors duration-200">Home</RouterLink>
          <i class="fa-solid fa-chevron-right text-xs text-white/30"></i>
          <span class="text-white/80">Our Products</span>
        </nav>
      </div>
    </section>

    <!-- -- Products ------------------------------------------------------------ -->
    <section class="py-20 lg:py-28 bg-white">
      <div class="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">

        <!-- Section header -->
        <div class="text-center max-w-2xl mx-auto mb-12" data-aos="fade-up">
          <div class="flex justify-center">
            <div class="section-label">Our Latest Products</div>
          </div>
          <h2 class="section-title">Some Of Our Products</h2>
          <p class="section-subtitle">
            Discover our range of high-quality medical equipment and supplies, sourced from trusted manufacturers to equip healthcare facilities across Uganda.
          </p>
        </div>

        <!-- Category filters -->
        <div class="flex flex-wrap justify-center gap-3 mb-12" data-aos="fade-up" data-aos-delay="100">
          <button
            v-for="cat in categories"
            :key="cat"
            @click="activeCategory = cat"
            :class="[
              'px-5 py-2 rounded-xl text-sm font-semibold transition-all duration-200',
              activeCategory === cat
                ? 'bg-primary text-white shadow-md'
                : 'bg-gray-100 text-gray-600 hover:bg-primary/10 hover:text-primary',
            ]"
          >
            {{ cat }}
          </button>
        </div>

        <!-- Products grid -->
        <div class="grid sm:grid-cols-2 lg:grid-cols-3 gap-6">
          <div
            v-for="(product, index) in filteredProducts"
            :key="product.name"
            class="product-card group bg-white rounded-2xl overflow-hidden shadow-card hover:shadow-card-lg hover:-translate-y-1.5 transition-all duration-300 border border-gray-100"
            data-aos="fade-up"
            :data-aos-delay="(index % 3) * 100"
          >
            <!-- Image area -->
            <div class="relative overflow-hidden bg-gray-50 aspect-square">
              <img
                :src="product.image"
                :alt="product.name"
                class="w-full h-full object-contain p-8 transition-transform duration-500 group-hover:scale-105"
                loading="lazy"
              />
              <!-- Category badge -->
              <span class="absolute top-4 left-4 text-[10px] font-bold uppercase tracking-wider text-primary bg-white px-3 py-1 rounded-full shadow-md border border-primary/10">
                {{ product.category }}
              </span>
              <!-- Overlay on hover -->
              <div class="product-overlay absolute inset-0 bg-gradient-to-t from-primary-darkest/85 via-primary/30 to-transparent flex items-end justify-center pb-8">
                <RouterLink
                  to="/contact-us"
                  class="bg-white text-primary text-xs font-bold px-6 py-2.5 rounded-xl hover:bg-primary hover:text-white transition-all duration-300 shadow-md"
                >
                  Enquire Now
                </RouterLink>
              </div>
            </div>

            <!-- Info -->
            <div class="p-5 border-t border-gray-100">
              <span class="text-[10px] font-bold uppercase tracking-widest text-secondary-dark mb-1.5 block">
                {{ product.category }}
              </span>
              <h3 class="font-bold text-gray-900 text-base group-hover:text-primary transition-colors duration-200 mb-1.5">
                {{ product.name }}
              </h3>
              <p class="text-gray-400 text-xs leading-relaxed">{{ product.desc }}</p>
            </div>
          </div>
        </div>

        <!-- Empty state -->
        <div v-if="filteredProducts.length === 0" class="text-center py-20">
          <div class="w-16 h-16 bg-gray-100 rounded-2xl flex items-center justify-center mx-auto mb-4">
            <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-7 h-7 text-gray-300">
              <path d="M21 16V8a2 2 0 00-1-1.73l-7-4a2 2 0 00-2 0l-7 4A2 2 0 003 8v8a2 2 0 001 1.73l7 4a2 2 0 002 0l7-4A2 2 0 0021 16z"/><polyline points="3.27 6.96 12 12.01 20.73 6.96"/><line x1="12" y1="22.08" x2="12" y2="12"/>
            </svg>
          </div>
          <p class="text-gray-400 font-medium">No products in this category yet.</p>
        </div>

        <!-- Contact CTA -->
        <div class="mt-16 bg-gradient-hero rounded-3xl p-8 sm:p-12 text-center relative overflow-hidden" data-aos="fade-up">
          <div class="absolute inset-0 hex-pattern opacity-50 pointer-events-none"></div>
          <div class="relative">
            <h3 class="text-2xl sm:text-3xl font-black text-white mb-3">Can't find what you need?</h3>
            <p class="text-white/65 mb-6 max-w-xl mx-auto">
              We supply a wide range of medical equipment beyond what's listed here. Contact us and we'll source exactly what your facility needs.
            </p>
            <RouterLink to="/contact-us" class="btn-white">
              <svg viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round" class="w-4 h-4">
                <line x1="22" y1="2" x2="11" y2="13"/><polygon points="22 2 15 22 11 13 2 9 22 2"/>
              </svg>
              Contact Us
            </RouterLink>
          </div>
        </div>

      </div>
    </section>

  </MainLayout>
</template>

<script setup>
import { ref, computed } from 'vue'
import MainLayout from './Layout/MainLayout.vue'

const activeCategory = ref('All')

const categories = [
  'All',
  'Medical Scales',
  'Laboratory Equipment',
  'Hospital Furniture',
  'CSSD',
]

const allProducts = [
  {
    name:     'Digital Weighing Scale',
    category: 'Medical Scales',
    desc:     'High-precision adult digital scale for clinical weight measurement. Capacity up to 200 kg with LCD display.',
    image:    require('../assets/orel-imgs/hd/adult-digital-weighing-scale.png'),
  },
  {
    name:     'Glucometer',
    category: 'Laboratory Equipment',
    desc:     'Accurate blood glucose monitoring device for diabetes management and routine patient monitoring.',
    image:    require('../assets/orel-imgs/hd/glucometer.png'),
  },
  {
    name:     'Microscope',
    category: 'Laboratory Equipment',
    desc:     'Professional binocular microscope for laboratory analysis, pathological examination, and research.',
    image:    require('../assets/orel-imgs/hd/microscope.png'),
  },
  {
    name:     'Electric ICU Beds',
    category: 'Hospital Furniture',
    desc:     'Fully electric multi-function ICU bed with adjustable positioning, side rails, and patient monitoring ports.',
    image:    require('../assets/orel-imgs/hd/obsteric-beds.png'),
  },
  {
    name:     'Patient Transfer Stretcher',
    category: 'Hospital Furniture',
    desc:     'Lightweight aluminum stretcher for safe and comfortable patient transport within healthcare facilities.',
    image:    require('../assets/orel-imgs/hd/patient-transfer-stretcher.png'),
  },
  {
    name:     'Portable Autoclave',
    category: 'CSSD',
    desc:     'Steam sterilization autoclave for CSSD departments. Ensures full sterilization of surgical instruments and equipment.',
    image:    require('../assets/orel-imgs/hd/portable-autoclave.png'),
  },
]

const filteredProducts = computed(() =>
  activeCategory.value === 'All'
    ? allProducts
    : allProducts.filter(p => p.category === activeCategory.value),
)
</script>
